import { useEffect, useId, useMemo, useState } from 'react';
import { Plus, Trash2, Link2, Check, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { UnitToggle } from '@/components/UnitToggle';
import { Results } from '@/components/Results';
import {
  Concern, CONCERN_LABELS, Room, UnitSystem, ROOM_PRESETS, parsePositive, validateRoom, mToFt, ftToM, sqmToSqft, sqftToSqm,
  GENERAL_TARGET, WILDFIRE_TARGET,
} from '@/lib/calculator';
import {
  DEFAULT_PREFS, Preferences, validatePrefs, GAS_SOURCE_LABELS, GasSource, FILTER_FACTORS, PrefErrors,
} from '@/lib/matching';
import { encodeShare, decodeShare, ShareState } from '@/lib/share';

interface RoomDraft {
  key: string;
  name: string;
  mode: 'dims' | 'area';
  lengthFt: number | null;
  widthFt: number | null;
  areaSqFt: number | null;
  ceilingFt: number | null;
  text: Record<'length' | 'width' | 'area' | 'ceiling', string>;
}

const fmtField = (v: number | null, dp = 2) => (v == null ? '' : String(+v.toFixed(dp)));
function draftText(d: Omit<RoomDraft, 'text'>, u: UnitSystem): RoomDraft['text'] {
  const len = (ft: number | null) => (ft == null ? null : u === 'metric' ? ftToM(ft) : ft);
  return {
    length: fmtField(len(d.lengthFt)), width: fmtField(len(d.widthFt)),
    area: fmtField(d.areaSqFt == null ? null : u === 'metric' ? sqftToSqm(d.areaSqFt) : d.areaSqFt, 1),
    ceiling: fmtField(len(d.ceilingFt)),
  };
}
let keySeq = 0;
function newDraft(u: UnitSystem, room?: Room, name?: string): RoomDraft {
  const base = {
    key: `r${++keySeq}`, name: name ?? 'Room 1', mode: room ? ('area' as const) : ('dims' as const),
    lengthFt: null, widthFt: null, areaSqFt: room?.areaSqFt ?? null, ceilingFt: room?.ceilingFt ?? 8,
  };
  return { ...base, text: draftText(base, u) };
}
export function draftToRoom(d: Pick<RoomDraft, 'mode' | 'lengthFt' | 'widthFt' | 'areaSqFt' | 'ceilingFt'>): Partial<Room> {
  const area = d.mode === 'dims' ? (d.lengthFt != null && d.widthFt != null ? d.lengthFt * d.widthFt : undefined) : d.areaSqFt ?? undefined;
  return { areaSqFt: area, ceilingFt: d.ceilingFt ?? undefined };
}

const CONCERN_ORDER: Concern[] = ['general', 'allergies', 'pets', 'dust', 'wildfire', 'gases'];

/** Text-state for numeric preference fields so typing is never fought */
interface PrefText { noise: string; buy: string; yr: string; each: string; hrs: string; kwh: string }
const prefToText = (p: Preferences): PrefText => ({
  noise: p.maxNoiseDbA?.toString() ?? '', buy: p.maxPurchaseUsd?.toString() ?? '', yr: p.maxAnnualUsd?.toString() ?? '',
  each: p.customEach?.toString() ?? '', hrs: String(p.costs.hoursPerDay), kwh: String(p.costs.tariffUsdPerKwh),
});

interface CalculatorProps {
  initialRoom?: Room;
  initialConcerns?: Concern[];
  /** Read/write the shareable state in the page URL (homepage only) */
  syncUrl?: boolean;
}

export function Calculator({ initialRoom, initialConcerns, syncUrl = false }: CalculatorProps) {
  const uid = useId();
  const [units, setUnits] = useState<UnitSystem>('imperial');
  const [rooms, setRooms] = useState<RoomDraft[]>(() => [newDraft('imperial', initialRoom)]);
  const [prefs, setPrefs] = useState<Preferences>({ ...DEFAULT_PREFS, concerns: initialConcerns ?? ['general'] });
  const [pt, setPt] = useState<PrefText>(prefToText(DEFAULT_PREFS));
  const [snapshot, setSnapshot] = useState<ShareState | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [linkMsgs, setLinkMsgs] = useState<{ error: string | null; warnings: string[] }>({ error: null, warnings: [] });
  const [copied, setCopied] = useState(false);

  const loadState = (s: ShareState) => {
    setUnits(s.units);
    setRooms(s.rooms.map((r, i) => newDraft(s.units, r, s.names[i])));
    setPrefs(s.prefs);
    setPt(prefToText(s.prefs));
    setSnapshot(s);
  };

  useEffect(() => {
    if (!syncUrl) return;
    const d = decodeShare(new URLSearchParams(window.location.search));
    setLinkMsgs({ error: d.error, warnings: d.warnings });
    if (d.state) loadState(d.state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Numeric text → prefs (invalid text yields NaN so validation flags it)
  const parsedPrefs: Preferences = useMemo(() => {
    const opt = (t: string) => (t.trim() === '' ? null : parsePositive(t) ?? NaN);
    const req = (t: string) => parsePositive(t) ?? NaN;
    return {
      ...prefs,
      maxNoiseDbA: opt(pt.noise), maxPurchaseUsd: opt(pt.buy), maxAnnualUsd: opt(pt.yr), customEach: opt(pt.each),
      costs: { ...prefs.costs, hoursPerDay: req(pt.hrs), tariffUsdPerKwh: req(pt.kwh) },
    };
  }, [prefs, pt]);
  const prefErrors: PrefErrors = validatePrefs(parsedPrefs);
  const gasOnly = prefs.concerns.every((c) => c === 'gases');
  const roomChecks = rooms.map((d) => validateRoom(draftToRoom(d)));
  const allValid = (gasOnly || roomChecks.every((v) => v.ok)) && (gasOnly || Object.keys(prefErrors).length === 0);

  const currentState: ShareState | null = allValid
    ? gasOnly
      ? { rooms: [], names: [], units, prefs: { ...DEFAULT_PREFS, country: prefs.country, concerns: ['gases'], gasSource: prefs.gasSource, gasPersistence: prefs.gasPersistence } }
      : { rooms: rooms.map((d) => draftToRoom(d) as Room), names: rooms.map((d) => d.name || 'Room'), units, prefs: parsedPrefs }
    : null;
  const stale = snapshot != null && (currentState == null || encodeShare(currentState).toString() !== encodeShare(snapshot).toString());

  const switchUnits = (u: UnitSystem) => {
    setUnits(u);
    setRooms((rs) => rs.map((d) => ({ ...d, text: draftText(d, u) })));
  };
  const updateField = (key: string, field: keyof RoomDraft['text'], text: string) => {
    setRooms((rs) => rs.map((d) => {
      if (d.key !== key) return d;
      const n = parsePositive(text);
      const ft = n == null ? null : field === 'area' ? (units === 'metric' ? sqmToSqft(n) : n) : units === 'metric' ? mToFt(n) : n;
      const map = { length: 'lengthFt', width: 'widthFt', area: 'areaSqFt', ceiling: 'ceilingFt' } as const;
      return { ...d, [map[field]]: ft, text: { ...d.text, [field]: text } };
    }));
  };
  const applyPreset = (key: string, presetId: string) => {
    const p = ROOM_PRESETS.find((x) => x.id === presetId);
    if (!p) return;
    setRooms((rs) => rs.map((d) => {
      if (d.key !== key) return d;
      const next = { ...d, mode: 'dims' as const, lengthFt: p.lengthFt, widthFt: p.widthFt, ceilingFt: p.ceilingFt, areaSqFt: null };
      return { ...next, text: draftText(next, units) };
    }));
  };
  const toggleConcern = (c: Concern, on: boolean) =>
    setPrefs((p) => {
      const next = on ? [...new Set([...p.concerns, c])] : p.concerns.filter((x) => x !== c);
      return { ...p, concerns: next.length ? next : ['general'] };
    });
  const setP = <K extends keyof Preferences>(k: K, v: Preferences[K]) => setPrefs((p) => ({ ...p, [k]: v }));

  const calculate = (e?: React.FormEvent) => {
    e?.preventDefault();
    setShowErrors(true);
    if (!currentState) {
      setTimeout(() => (document.querySelector('[aria-invalid="true"]') as HTMLElement | null)?.focus(), 0);
      return;
    }
    setSnapshot(currentState);
    if (syncUrl) {
      window.history.replaceState({}, '', `${window.location.pathname}?${encodeShare(currentState).toString()}`);
      setLinkMsgs({ error: null, warnings: [] });
    }
    setTimeout(() => document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const shareUrl = snapshot ? `${window.location.origin}/?${encodeShare(snapshot).toString()}` : '';
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { window.prompt('Copy this link:', shareUrl); }
  };

  const lenUnit = units === 'metric' ? 'm' : 'ft';
  const areaUnit = units === 'metric' ? 'm²' : 'ft²';
  const hasGas = prefs.concerns.includes('gases');
  const particleConcerns = prefs.concerns.filter((c) => c !== 'gases');
  const err = (k: keyof PrefErrors) => (showErrors ? prefErrors[k] : undefined);

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-5 sm:p-6">
          <form onSubmit={calculate} noValidate className="space-y-6" aria-label="Air purifier calculator">
            {(linkMsgs.error || linkMsgs.warnings.length > 0) && (
              <div role="status" className="rounded-md border border-border bg-muted p-3 text-sm text-foreground space-y-1">
                {linkMsgs.error && <p className="font-medium">{linkMsgs.error}</p>}
                {linkMsgs.warnings.map((w) => <p key={w}>{w}</p>)}
              </div>
            )}

            <fieldset className="space-y-2">
              <legend className="text-sm font-semibold text-foreground">1. Where are you buying?</legend>
              <div className="flex flex-wrap gap-4 text-sm">
                {([['US', 'United States'], ['other', 'Another country (sizing only)']] as const).map(([v, l]) => (
                  <label key={v} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name={`${uid}-country`} checked={prefs.country === v} onChange={() => setP('country', v)} /> {l}
                  </label>
                ))}
              </div>
              {prefs.country === 'other' && <p className="text-xs text-muted-foreground">Product data is US-only for now. You still get the sizing target.</p>}
            </fieldset>

            <fieldset className="space-y-2">
              <legend className="text-sm font-semibold text-foreground">2. What are you dealing with?</legend>
              <div className="grid sm:grid-cols-2 gap-2">
                {CONCERN_ORDER.map((c) => (
                  <label key={c} className="flex items-center gap-2 rounded-md border border-border p-3 text-sm cursor-pointer has-[:checked]:border-primary">
                    <Checkbox checked={prefs.concerns.includes(c)} onCheckedChange={(v) => toggleConcern(c, v === true)} />
                    {CONCERN_LABELS[c]}
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Particle sizing uses {GENERAL_TARGET.each} eACH by default, or {WILDFIRE_TARGET.each} eACH with smoke CADR for wildfire smoke. Each particle concern is checked against its own CADR (smoke, dust or pollen). Gases are handled separately — particle CADR says nothing about them.
              </p>
              {hasGas && (
                <div className="grid sm:grid-cols-2 gap-3 rounded-md border border-border p-3">
                  <div className="space-y-1.5">
                    <Label htmlFor={`${uid}-gs`} className="text-xs">Likely source of the odour/gas</Label>
                    <select id={`${uid}-gs`} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={prefs.gasSource ?? ''}
                      onChange={(e) => setP('gasSource', (e.target.value || null) as GasSource | null)}>
                      <option value="">Choose…</option>
                      {Object.entries(GAS_SOURCE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`${uid}-gp`} className="text-xs">How often?</Label>
                    <select id={`${uid}-gp`} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={prefs.gasPersistence ?? ''}
                      onChange={(e) => setP('gasPersistence', (e.target.value || null) as Preferences['gasPersistence'])}>
                      <option value="">Choose…</option>
                      <option value="occasional">Occasionally (e.g. when cooking)</option>
                      <option value="ongoing">Most of the time</option>
                    </select>
                  </div>
                </div>
              )}
            </fieldset>

            {particleConcerns.length > 0 && (
              <fieldset className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <legend className="text-sm font-semibold text-foreground">3. Room size</legend>
                  <UnitToggle value={units} onChange={switchUnits} />
                </div>
                {rooms.map((d, i) => {
                  const v = roomChecks[i];
                  const id = `${uid}-${d.key}`;
                  const bad = showErrors && !v.ok;
                  return (
                    <div key={d.key} className="rounded-lg border border-border p-4 space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <Input aria-label={`Name for room ${i + 1}`} value={d.name} maxLength={40}
                          onChange={(e) => setRooms((rs) => rs.map((x) => (x.key === d.key ? { ...x, name: e.target.value } : x)))}
                          className="h-9 max-w-[14rem] font-medium" />
                        {rooms.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" className="min-h-11 min-w-11" onClick={() => setRooms((rs) => rs.filter((x) => x.key !== d.key))} aria-label={`Remove ${d.name || `room ${i + 1}`}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2" role="group" aria-label={`Presets for ${d.name}`}>
                        {ROOM_PRESETS.map((p) => <Button key={p.id} type="button" variant="outline" size="sm" onClick={() => applyPreset(d.key, p.id)}>{p.label}</Button>)}
                      </div>
                      <div className="flex gap-4 text-sm" role="radiogroup" aria-label="How to enter size">
                        {(['dims', 'area'] as const).map((m) => (
                          <label key={m} className="flex items-center gap-2 cursor-pointer">
                            <input type="radio" name={`${id}-mode`} checked={d.mode === m} onChange={() => setRooms((rs) => rs.map((x) => (x.key === d.key ? { ...x, mode: m } : x)))} />
                            {m === 'dims' ? 'Length × width' : 'Floor area'}
                          </label>
                        ))}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {d.mode === 'dims' ? (
                          <>
                            <Field id={`${id}-l`} label={`Length (${lenUnit})`} value={d.text.length} invalid={bad && !!v.errors.area} onChange={(t) => updateField(d.key, 'length', t)} />
                            <Field id={`${id}-w`} label={`Width (${lenUnit})`} value={d.text.width} invalid={bad && !!v.errors.area} onChange={(t) => updateField(d.key, 'width', t)} />
                          </>
                        ) : (
                          <Field id={`${id}-a`} label={`Floor area (${areaUnit})`} value={d.text.area} invalid={bad && !!v.errors.area} onChange={(t) => updateField(d.key, 'area', t)} />
                        )}
                        <Field id={`${id}-h`} label={`Ceiling (${lenUnit})`} value={d.text.ceiling} invalid={bad && !!v.errors.ceiling} onChange={(t) => updateField(d.key, 'ceiling', t)} />
                      </div>
                      {bad && <div role="alert" className="text-sm text-destructive space-y-0.5">{Object.values(v.errors).map((e) => <p key={e}>{e}</p>)}</div>}
                    </div>
                  );
                })}
                <Button type="button" variant="outline" size="sm" disabled={rooms.length >= 10}
                  onClick={() => setRooms((rs) => [...rs, newDraft(units, undefined, `Room ${rs.length + 1}`)])}>
                  <Plus className="h-4 w-4 mr-1" /> Add a separate room
                </Button>
                <p className="text-xs text-muted-foreground">Each room is sized on its own. A purifier in one room does not clean a closed room next door.</p>
              </fieldset>
            )}

            {particleConcerns.length > 0 && (
              <details className="rounded-lg border border-border p-4" open={showErrors && Object.keys(prefErrors).length > 0}>
                <summary className="cursor-pointer text-sm font-semibold text-foreground">4. Noise, budget, running cost and other preferences (optional)</summary>
                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                  <Field id={`${uid}-noise`} label="Maximum noise (dBA, at the speed used)" placeholder="No limit" value={pt.noise} error={err('noise')} onChange={(t) => setPt({ ...pt, noise: t })} />
                  <Field id={`${uid}-buy`} label="Purchase budget (USD, all units)" placeholder="No limit" value={pt.buy} error={err('purchase')} onChange={(t) => setPt({ ...pt, buy: t })} />
                  <Field id={`${uid}-yr`} label="Yearly running budget (USD)" placeholder="No limit" value={pt.yr} error={err('annual')} onChange={(t) => setPt({ ...pt, yr: t })} />
                  <Field id={`${uid}-each`} label="Custom air changes per hour (replaces default)" placeholder="Default" value={pt.each} error={err('each')} onChange={(t) => setPt({ ...pt, each: t })} />
                  <Field id={`${uid}-hrs`} label="Hours run per day" value={pt.hrs} error={err('hours')} onChange={(t) => setPt({ ...pt, hrs: t })} />
                  <Field id={`${uid}-kwh`} label="Electricity price (USD per kWh)" value={pt.kwh} error={err('tariff')} onChange={(t) => setPt({ ...pt, kwh: t })} />
                  <SelectField id={`${uid}-ff`} label="Filter life vs. manufacturer interval" value={String(prefs.costs.filterIntervalFactor)}
                    onChange={(v) => setP('costs', { ...prefs.costs, filterIntervalFactor: Number(v) })}
                    options={FILTER_FACTORS.map((f) => [String(f), f === 1 ? 'As stated by the manufacturer' : f < 1 ? `${f * 100}% as long (dusty/smoky home)` : `${f}× as long`])} />
                  <SelectField id={`${uid}-ion`} label="Ionizer / electronic stage" value={prefs.ionizer} onChange={(v) => setP('ionizer', v as Preferences['ionizer'])}
                    options={[['any', 'No preference'], ['off-or-none', 'Only if switchable off, with output tested off'], ['none', 'Avoid units with one']]} />
                  <SelectField id={`${uid}-sort`} label="Sort results by" value={prefs.sort} onChange={(v) => setP('sort', v as Preferences['sort'])}
                    options={[['three-year', 'Lowest 3-year estimated cost'], ['purchase', 'Lowest purchase price'], ['noise', 'Lowest measured noise'], ['output', 'Highest clean-air output']]} />
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={prefs.allowTwoUnits} onCheckedChange={(v) => setP('allowTwoUnits', v === true)} />
                    I'm open to two units in the same open space
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={prefs.diyOnly} onCheckedChange={(v) => setP('diyOnly', v === true)} />
                    Show DIY / fan-based builds only
                  </label>
                </div>
              </details>
            )}

            <Button type="submit" size="lg" className="w-full">Calculate</Button>
          </form>
        </CardContent>
      </Card>

      {snapshot && (
        <section id="results" aria-live="polite" className="scroll-mt-20 space-y-4">
          {stale && (
            <div role="status" className="flex gap-2 items-start rounded-md border border-border bg-muted p-3 text-sm text-foreground">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>You've changed your inputs. These results (and the share link) still reflect the last calculation — press Calculate to update.</span>
            </div>
          )}
          <div className="flex justify-end">
            <Button type="button" variant="outline" size="sm" onClick={copyLink}>
              {copied ? <Check className="h-4 w-4 mr-1" /> : <Link2 className="h-4 w-4 mr-1" />}
              {copied ? 'Link copied' : 'Copy link to these results'}
            </Button>
          </div>
          <Results state={snapshot} />
        </section>
      )}
    </div>
  );
}

function Field({ id, label, value, onChange, placeholder, error, invalid }: { id: string; label: string; value: string; onChange: (t: string) => void; placeholder?: string; error?: string; invalid?: boolean }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs">{label}</Label>
      <Input id={id} inputMode="decimal" autoComplete="off" value={value} placeholder={placeholder} aria-invalid={!!error || !!invalid}
        aria-describedby={error ? `${id}-err` : undefined} onChange={(e) => onChange(e.target.value)} />
      {error && <p id={`${id}-err`} className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function SelectField({ id, label, value, onChange, options }: { id: string; label: string; value: string; onChange: (v: string) => void; options: (readonly [string, string])[] }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs">{label}</Label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
